// DEPENDENCIES ---------------------------------------------------------------- //

import dayjs from 'dayjs';

import { ReportingRange } from '../types';

// CONSTANTS ---------------------------------------------------------------- //

export const REPORTING_RANGES: ReportingRange[] = [
  'last7',
  'last30',
  'last90',
  'rolling30',
  'thisMonth',
  'lastMonth',
  'custom',
];

export const DEFAULT_REPORTING_RANGE: ReportingRange = 'last30';

// GETTERS ---------------------------------------------------------------- //

/** Validate that a stored string is a known reporting range */
export function isValidReportingRange(value?: string | null): value is ReportingRange {
  return !!value && REPORTING_RANGES.includes(value as ReportingRange);
}

/** Get the start date (ISO string) for a reporting range */
export function getRangeStart(range: ReportingRange, customStart?: string | null): string {
  switch (range) {
    case 'last7':
      return dayjs().subtract(6, 'day').startOf('day').format();
    case 'last30':
      return dayjs().subtract(29, 'day').startOf('day').format();
    case 'last90':
      return dayjs().subtract(89, 'day').startOf('day').format();
    case 'rolling30':
      return dayjs().subtract(15, 'day').startOf('day').format();
    case 'thisMonth':
      return dayjs().startOf('month').format();
    case 'lastMonth':
      return dayjs().subtract(1, 'month').startOf('month').format();
    case 'custom': {
      const minDate = dayjs().startOf('day').subtract(1, 'year').format();
      const fallback = dayjs().subtract(29, 'day').startOf('day').format();
      if (!customStart || customStart === 'Invalid Date') return fallback;
      if (customStart < minDate) return minDate;
      return customStart;
    }
    default:
      return dayjs().subtract(29, 'day').startOf('day').format();
  }
}

/** Get the end date (ISO string) for a reporting range */
export function getRangeEnd(range: ReportingRange, customEnd?: string | null): string {
  switch (range) {
    case 'last7':
    case 'last30':
    case 'last90':
      return dayjs().endOf('day').format();
    case 'rolling30':
      return dayjs().add(15, 'day').endOf('day').format();
    case 'thisMonth':
      return dayjs().endOf('month').format();
    case 'lastMonth':
      return dayjs().subtract(1, 'month').endOf('month').format();
    case 'custom': {
      const maxDate = dayjs().endOf('day').add(1, 'year').format();
      const fallback = dayjs().endOf('day').format();
      if (!customEnd || customEnd === 'Invalid Date') return fallback;
      if (customEnd > maxDate) return maxDate;
      return customEnd;
    }
    default:
      return dayjs().endOf('day').format();
  }
}

/** List each calendar day (YYYY-MM-DD) between start and end inclusive */
export function listDaysInRange(start: string, end: string): string[] {
  const days: string[] = [];
  let cursor = dayjs(start).startOf('day');
  const last = dayjs(end).startOf('day');
  // Guard against inverted/invalid ranges
  if (!cursor.isValid() || !last.isValid() || cursor.isAfter(last)) return days;
  // Hard cap at ~2 years of days to avoid runaway loops on bad input
  let safety = 750;
  while ((cursor.isBefore(last) || cursor.isSame(last)) && safety > 0) {
    days.push(cursor.format('YYYY-MM-DD'));
    cursor = cursor.add(1, 'day');
    safety--;
  }
  return days;
}
